import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * 並べ替えを保存するまでの待ち時間。
 * 保存ボタンを出さない代わりに、操作が終わってからこの時間で送る。
 * 1 手ごとに送ると自動配車 (lockType: 'sequence') が連続で走って計算が詰まる。
 */
export const SAVE_DEBOUNCE_MS = 2000;

/**
 * 最後の操作から SAVE_DEBOUNCE_MS 後に save を呼ぶ。
 * 続けて schedule すると待ち直し、最後の値だけを送る。
 *
 * ポインタを押している間は操作中とみなして送らず、離してから数え直す。
 * 1 手目を落として 2 手目を掴んでいる間に時間が来ると、動かしている途中で
 * 自動配車が始まってしまう (log_web_issues#337)。
 *
 * Why not ドラッグ開始 (dnd-kit の onDragStart) を受けて止める: 停車地と回転の DndContext は
 * カード配車と共有する一覧の奥にあり、開始の通知を 4 つのコンポーネントに通すことになる。
 * 掴んでも 5px 動かさなければドラッグにならない (activationConstraint) ので、その場合の
 * 再開も別に要る。押している間は何であれ操作中として待つほうが漏れが無い
 */
export function useDebouncedSave<T>(save: (payload: T) => void) {
  const [isPending, setIsPending] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // 送るのを待っている値。押している間はタイマーを止め、これだけを持っておく
  const pendingRef = useRef<{ payload: T } | null>(null);
  const pointerDownRef = useRef(false);
  // save を依存に入れると、呼び出し側が毎回作り直したときに schedule の同一性が壊れる
  const saveRef = useRef(save);
  saveRef.current = save;

  const clear = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const startTimer = useCallback(() => {
    clear();
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      const pending = pendingRef.current;
      pendingRef.current = null;
      setIsPending(false);
      if (pending) saveRef.current(pending.payload);
    }, SAVE_DEBOUNCE_MS);
  }, [clear]);

  const schedule = useCallback((payload: T) => {
    pendingRef.current = { payload };
    setIsPending(true);
    // dnd-kit の onDragEnd は pointerup と前後しうる。押したままなら離したときに数え始める
    if (pointerDownRef.current) clear();
    else startTimer();
  }, [clear, startTimer]);

  /** 取り消したあとに送ると、画面に無い並びが保存される */
  const cancel = useCallback(() => {
    clear();
    pendingRef.current = null;
    setIsPending(false);
  }, [clear]);

  useEffect(() => {
    const handleDown = () => {
      pointerDownRef.current = true;
      clear();
    };
    const handleUp = () => {
      pointerDownRef.current = false;
      if (pendingRef.current) startTimer();
    };
    // capture で受ける。dnd-kit や MUI が伝播を止めても取りこぼさない
    window.addEventListener('pointerdown', handleDown, true);
    window.addEventListener('pointerup', handleUp, true);
    window.addEventListener('pointercancel', handleUp, true);
    return () => {
      window.removeEventListener('pointerdown', handleDown, true);
      window.removeEventListener('pointerup', handleUp, true);
      window.removeEventListener('pointercancel', handleUp, true);
      // 画面を離れたあとに送ると、戻ってきたとき身に覚えのない並びになっている
      clear();
    };
  }, [clear, startTimer]);

  return { schedule, cancel, isPending };
}
